import {
  BOOLEAN_SELECT_LIST_ITEMS,
  Box,
  Grid,
  Stack,
  pn,
  useElementBreakpoints
} from "@compass/core";
import { CustomFilters, DatePicker, Select } from "components";
import { CustomEntityOption } from "components/common/CustomFilters/CustomFilter";
import {
  CustomFilterEntity,
  NonPersonnelFilter as NonPersonnelFilterModel,
  NonPersonnelFilterResource
} from "generated/openApi";
import produce from "immer";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";

export const NonPersonnelFilter = (props: {
  filter: NonPersonnelFilterModel;
  setFilter: (value: NonPersonnelFilterModel) => void;
}) => {
  const { breakpointsRef, smallFieldBreakpoints } = useElementBreakpoints();
  const [resource, setResource] = useState<NonPersonnelFilterResource>({});
  const properties = resource.properties || [];
  const filteredSubPartners = resource.subPartners?.filter((x) => x.partnerId === props.filter.partnerId) || [];

  const entityOptions: CustomEntityOption[] = [
    { EntityType: CustomFilterEntity.NonPersonnel, EntityName: "Non-Personnel" },
  ];

  useEffect(() => {
    ApiService.nonPersonnels.getFilterResource().then((response) => {
      setResource(response);
    });
  }, []);

  const partnerField = () => {
    const propertyName = pn<NonPersonnelFilterModel>("partnerId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        value={resource.partners?.find((x) => x.id === props.filter.partnerId)}
        options={resource.partners}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.partnerId = value?.id;
            })
          )
        }
      />
    );
  };

  const subPartnerField = () => {
    const propertyName = pn<NonPersonnelFilterModel>("subPartnerId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        value={filteredSubPartners.find((x) => x.id === props.filter.partnerId)}
        options={filteredSubPartners}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.subPartnerId = value?.id;
            })
          )
        }
      />
    );
  };

  const dateFromField = () => {
    const propertyName = pn<NonPersonnelFilterModel>("dateFrom");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        value={props.filter.dateFrom}
        onChange={(value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.dateFrom = value;
            })
          )
        }
      />
    );
  };

  const dateToField = () => {
    const propertyName = pn<NonPersonnelFilterModel>("dateTo");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        value={props.filter.dateTo}
        onChange={(value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.dateTo = value;
            })
          )
        }
      />
    );
  };

  const isMatchField = () => {
    const propertyName = pn<NonPersonnelFilterModel>("isMatch");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.filter.isMatch)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.isMatch = value?.id;
            })
          )
        }
      />
    );
  };

  const reviewField = () => {
    const propertyName = pn<NonPersonnelFilterModel>("reviewId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        value={resource.matchReviews?.find((x) => x.id === props.filter.reviewId)}
        options={resource.matchReviews}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.reviewId = value?.id;
            })
          )
        }
      />
    );
  };

  const hasFileField = () => {
    const propertyName = pn<NonPersonnelFilterModel>("hasFile");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.filter.hasFile)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.hasFile = value?.id;
            })
          )
        }
      />
    );
  };

  return (
    <Stack ref={breakpointsRef} spacing="lg">
      <CustomFilters
        allowedEntityTypes={entityOptions}
        filters={props.filter.customFilters}
        setFilters={(filters) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.customFilters = filters;
            })
          )
        }
        properties={resource.customFilterProperties}
      />

      <Box>
        <Grid container spacing="sm">
          <Grid item xs={smallFieldBreakpoints}>
            {partnerField()}
          </Grid>

          {filteredSubPartners.length > 0 && (
            <Grid item xs={smallFieldBreakpoints}>
              {subPartnerField()}
            </Grid>
          )}

          <Grid item xs={smallFieldBreakpoints}>
            {dateFromField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {dateToField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {isMatchField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {reviewField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {hasFileField()}
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
};
